'use client';
import Pagination from '@/components/CommonComponents/Pagination';
import StatCard from '@/components/CommonComponents/StatCard';

import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey } from '@/helper/utils';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { notificationManagementService } from '@/services/notificationManagementService/notificationManagementService';
import { AppDispatch, RootState } from '@/store';
import {
  fetchNotificationList,
  getNotificationsInfo,
  resendNotification,
} from '@/store/slices/notificationManagementSlice/notificationManagementSlice';
import { getProjectsDropdown } from '@/store/slices/projectsSlice/projectsSlice';
import {
  clearSitesByProject,
  getSitesByProjectId,
  SitesDropdown,
} from '@/store/slices/siteManagementSlice/siteManagementSlice';
import { Notifications } from '@/types/notification.type';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import DataTable, { Column } from '../CommonComponents/DataTable';
import ServerSearchFilter from '../CommonComponents/ServerSearchFilter';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';
import ResendNotification from './CreateNotification/ResendNotification';

export default function NotificationManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { exportFile } = useExportFile();
  const [selectedNotification, setSelectedNotification] = useState<Notifications | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentPage, setcurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [resendModal, setResendModal] = useState<boolean>(false);
  const { list, loading } = useSelector((state: RootState) => state.notification);

  const { data, meta } = list;
  // const {page , limit , }

  const projectList = useSelector((state: RootState) => state.projects.list.projectsDropdown);
  const sitesList = useSelector((state: RootState) => state.sites);
  const notificationCount = useSelector((state: RootState) => state.notification.notificationInfo);

  // Get project ID from navigation state
  const incomingProjectId = location.state?.projectId;

  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({
    page: null,
    limit: null,
    search: null,
    status: null,
    notificationType: null,
    targetGroup: null,
    projectId: null,
    siteId: null,
  });

  const permissions = {
    create: usePermission('write', 'notifications'),
    edit: usePermission('edit', 'notifications'),
    delete: usePermission('delete', 'notifications'),
    export: usePermission('export', 'notifications'),
  };

  const filteredData = data?.map((each) => ({
    ...each,
    tableData: each.status ?? undefined,
    _permissions: permissions,
  }));

  const projectMap = useMemo(() => {
    return new Map(projectList?.map((p) => [p.id, p.name]));
  }, [projectList]);

  useEffect(() => {
    dispatch(
      fetchNotificationList({
        ...searchFilters,
        projectId: incomingProjectId ? String(incomingProjectId) : searchFilters.projectId,
      }),
    );
    dispatch(getNotificationsInfo());
  }, [dispatch, searchFilters, incomingProjectId]);

  useEffect(() => {
    dispatch(getProjectsDropdown());
  }, [dispatch]);

  useEffect(() => {
    if (searchFilters.projectId) {
      dispatch(getSitesByProjectId(searchFilters.projectId));
    } else {
      dispatch(clearSitesByProject());
      dispatch(SitesDropdown());
    }
  }, [dispatch, searchFilters.projectId]);

  useEffect(() => {
    setSearchFilters((prev) => ({ ...prev, page: currentPage, limit }));
  }, [currentPage, limit]);

  const handleEditNotification = (row: Notifications) => {
    // const canEdit = row.deliveryType === 'SCHEDULED' || row.status === 'DRAFT';
    const canEdit = row.status !== 'SENT';
    if (canEdit) {
      navigate('/dashboard/notification-management/create?mode=edit', {
        state: { notification: row },
      });
    } else {
      showToast.error(`This notification can't be edited`);
    }
  };

  const handleFilterChange = (filtered: Record<string, any>) => {
    // Mapping UI labels → Backend values
    const map: Record<string, string> = {
      // Notification Types
      Push: 'PUSH_NOTIFICATION',
      'In-app': 'IN_APP_NOTIFICATION',
      Both: 'BOTH',

      // Status
      Scheduled: 'SCHEDULED',
      Sent: 'SENT',
      Draft: 'DRAFT',
      Failed: 'FAILED',

      // Target Group
      'Filtered Users': 'FILTER_USERS',
      'Bulk Upload': 'BULK_USERS',
      'All Users': 'ALL_USERS',
    };

    let projectId: string | null = null;
    let siteId: string | null = null;

    // Project selection
    if (filtered.project) {
      const project = projectList?.find((p) => p.name === filtered.project);
      projectId = project ? String(project.id) : null;
      if (projectId !== filtered.projectId) {
        dispatch(clearSitesByProject());
        filtered.site = null;
        filtered.siteId = null;
      }
      filtered.projectId = projectId;
    } else {
      filtered.projectId = null;
    }

    // Site selection
    if (filtered.site) {
      const site = sitesList?.sitesDropdown?.data?.find((p) => p.name === filtered.site);
      filtered.siteId = site ? String(site.id) : null;
    } else {
      filtered.siteId = null;
    }

    setSearchFilters((prev) => ({
      ...prev,
      notificationType: filtered.notificationType ? map[filtered.notificationType] : null,
      status: filtered.status ? map[filtered.status] : null,
      targetGroup: filtered.targetGroup ? map[filtered.targetGroup] : null,
      projectId: filtered.projectId || null,
      siteId: filtered.siteId || null,
      search: filtered.search ?? prev.search,
      page: 1,
      limit,
    }));

    setcurrentPage(1);
  };

  const handleRotate = (row: any) => {
    const payload = {
      title: row.title,
      message: row.message,
      projectId: row?.receivers[0]?.projectId,
      siteId: row?.receivers[0]?.siteId,
      fileUrl: row.fileUrl || undefined,
      surveySetId: row?.receivers[0]?.surveySetId,
      posterCode: undefined,
      notificationType: row.notificationType,
      targetGroup: (row.targetGroup as any) || 'ALL_USERS',
      deliveryType: row.deliveryType,
      scheduledTimer: row.scheduledTimer || new Date().toISOString(),
      isTested: false,
      status: null,
    } as any;

    dispatch(resendNotification(row?.id) as any)
      .unwrap()
      .then(() => {
        showToast.success('Notification resent successfully');
        dispatch(fetchNotificationList({ ...searchFilters }));
        dispatch(getNotificationsInfo());
        setResendModal(false);
      })
      .catch((err: any) => {
        const msg = err?.toString?.() || 'Failed to resend notification';
        showToast.error(msg);
      });
  };

  const confrimResendNotification = (data: Notifications | null) => {
    setSelectedNotification(data);
    setResendModal(!resendModal);
  };

  const handleAddNotification = () => {
    navigate('/dashboard/notification-management/create');
  };

  const handleExport = useCallback(() => {
    exportFile(
      () => notificationManagementService.exportNotifications(),
      `notifications_${Date.now()}.csv`,
    );
  }, [exportFile]);

  const notificationColumns: Column<Notifications>[] = [
    { key: 'id', header: 'Notification ID' },
    { key: 'title', header: 'Title / Subject' },

    {
      key: 'message',
      header: 'Message Preview',
      render: (value) => <p className="truncate max-w-[220px]">{value}</p>,
    },

    {
      key: 'projectId',
      header: 'Linked Project',
      render: (_value, rowData: Notifications) => {
        const projectName = rowData.project?.name || 'N/A';
        return (
          <span
            key={`project-${rowData.id}`}
            className="px-4 py-1 rounded-full text-sm bg-[#C9C9C9CC]"
          >
            {projectName}
          </span>
        );
      },
    },

    {
      key: 'siteId',
      header: 'Linked Site',
      render: (_value, rowData: Notifications) => {
        const siteName = rowData.project?.site?.name || 'N/A';
        return <span key={`site-${rowData?.id}`}>{siteName}</span>;
      },
    },

    {
      key: 'notificationType',
      header: 'Type',
      render: (value) => {
        const formatted = value
          ?.toLowerCase()
          .replace('notification', '') // remove the word "notification"
          .replace(/_/g, '') // remove underscores
          .trim()
          .replace('inapp', 'in_app'); // rename inapp → in_app

        return (
          <span
            className={`px-4 py-1 rounded-full text-sm ${
              formatted === 'push'
                ? 'bg-[#F17853CC] text-[#1A0206]'
                : formatted === 'in_app'
                ? 'bg-[#A3CFC3] text-[#1A0206]'
                : 'bg-[#537DF1] text-[#1A0206]'
            }`}
          >
            {formatted === 'in_app' ? 'In-App' : formatted}
          </span>
        );
      },
    },
    {
      key: 'targetGroup',
      header: 'Target Audience',
      render: (value) => <span>{value ? desanitizeKey(value) : 'N/A'}</span>,
    },
    {
      key: 'deliveryType',
      header: 'Delivery Type',
      render: (value) => <span>{value ? desanitizeKey(value) : 'N/A'}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created On',
      render: (value) => value.split('T')[0],
    },
    {
      key: 'scheduledTimer',
      header: 'Scheduled Send',
      render: (value) => (
        <span>
          {value
            ? `${new Date(value).toLocaleDateString('en-GB').replace(/\//g, '-')} ${new Date(
                value,
              ).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}`
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'tableData',
      header: 'Status',
      render: (value) => <span> {value ? value.toLowerCase() : 'N/A'}</span>,
    },
  ];

  const getNotificationDetailFields = (data: Notifications | null) => {
    if (!data) return [];

    const fields = [
      { label: 'Notification ID', value: data.id, half: true },
      { label: 'Notification Title', value: data.title, half: true },

      { label: 'Message', value: data.message, fullWidth: true, colSpan: 2 },

      {
        label: 'Notification Type',
        value: data.notificationType
          ? data.notificationType
              .toLowerCase()
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase())
          : '--',
        half: true,
      },
      {
        label: 'Audience Type',
        value: data.targetGroup
          ? data.targetGroup
              .toLowerCase()
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase())
          : '--',
        half: true,
      },
    ];

    // Show Linked Projects and Sites if project data is available
    if (data.project?.name || data.targetGroup === 'FILTER_USERS') {
      fields.push(
        {
          label: 'Linked Projects',
          value: [data.project?.name || 'N/A'],
          list: true,
          half: true,
        } as any,
        {
          label: 'Linked Sites',
          value: [data.project?.site?.name || 'N/A'],
          list: true,
          half: true,
        } as any,
      );
    }

    fields.push({ label: 'Created On', value: data.createdAt?.split('T')[0], half: true });

    // Show Scheduled Send and Date only if deliveryType is SCHEDULED
    if (data.deliveryType === 'SCHEDULED') {
      fields.push(
        {
          label: 'Scheduled Date',
          value: data.scheduledTimer?.split('T')[0] || '--',
          half: true,
        },
        {
          label: 'Scheduled Send',
          value: data.scheduledTimer
            ? `${new Date(data.scheduledTimer)
                .toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
                .toUpperCase()} ${((data as any).timeZone || 'UTC').toUpperCase()}`
            : '--',
          half: true,
        },
      );
    }

    if (data.fileUrl) {
      const fileName = data.fileUrl.split('/').pop() || data.fileUrl;
      fields.push({ label: 'Attachments', value: fileName, half: true });
      fields.push({ label: 'Status', value: data.status || 'Draft', half: true });
    } else {
      fields.push({ label: 'Status', value: data.status || 'Draft', half: true });
    }

    return fields;
  };

  const notificationStats = [
    {
      id: 'total',
      title: 'Total Notifications',
      value: notificationCount.data.totalNotification,
      icon: Bell,
      iconBg: 'bg-[#0BA96FCC]',
    },
    {
      id: 'scheduled',
      title: 'Scheduled Notifications',
      value: notificationCount.data.totalScheduledNotification,
      icon: BellRing,
      iconBg: 'bg-[#F4A12CCC]',
    },
    {
      id: 'delete',
      title: 'Draft Notifications',
      value: notificationCount.data.draftNotification,
      icon: BellOff,
      iconBg: 'bg-[#8E4370CC]',
    },
  ];

  const projectOptions = [
    ...(projectList?.map((each) => ({
      label: each.name,
      value: String(each.id),
    })) || []),
  ];

  const siteOptions: { label: string; value: string }[] = [
    ...(searchFilters.projectId
      ? sitesList.sitesByProject.map((site) => ({ label: site.name, value: String(site.id) }))
      : sitesList.sitesDropdown?.data.map((site) => ({
          label: site.name,
          value: String(site.id),
        })) || []),
  ];

  return (
    <PageHeaderWrapper
      title="Notification Management"
      description="Manage and monitor all notifications across the platform."
      onAdd={handleAddNotification}
      onExport={handleExport}
      addLabel="Create New Notification"
      exportLabel="Export"
      permissions={permissions}
    >
      <div className="flex-shrink-0 mb-4 space-y-4">
        {/* Search + Filter */}
        <ServerSearchFilter
          fields={[
            {
              type: 'search',
              key: 'search',
              placeholder: 'Search notifications by title, message, or ID...',
            },
            {
              type: 'radio-select',
              key: 'status',
              label: 'Status',
              options: ['Scheduled', 'Sent', 'Draft', 'Failed'],
            },
            {
              type: 'radio-select',
              key: 'notificationType',
              label: 'Types',
              options: ['Push', 'In-app', 'Both'],
            },
            {
              type: 'radio-select',
              key: 'targetGroup',
              label: 'Audiences',
              options: ['All Users', 'Filtered Users', 'Bulk Upload'],
            },

            {
              type: 'radio-select',
              key: 'project',
              label: 'Projects',
              options: projectOptions.map((options) => options.label),
              className:
                'flex relative flex-col min-w-[220px] border border-gray-300 rounded-lg bg-[#F2E9EA] text-sm',
            },
            {
              type: 'radio-select',
              key: 'site',
              label: 'Sites',
              options: siteOptions.map((options: any) => options.label),
              className:
                'flex relative flex-col min-w-[180px] border border-gray-300 rounded-lg bg-[#F2E9EA] text-sm',
              // disabled: !searchFilters.projectId,
            },
          ]}
          onChange={handleFilterChange}
        />
        {resendModal && (
          <ResendNotification
            isEdit={true}
            details={selectedNotification}
            onAccept={() => {
              handleRotate(selectedNotification);
            }}
            onClose={() => {
              setResendModal(false);
            }}
          />
        )}
        {/**Stats */}
        <div className="flex-shrink-0 mb-4">
          <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {notificationStats.map((stat) => (
              <StatCard
                key={stat.id}
                icon={stat.icon}
                iconBg={stat.iconBg}
                title={stat.title}
                value={stat.value}
              />
            ))}
          </div>
        </div>

        <div>
          {/* Table */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <DataTable
              listTitle=""
              columns={notificationColumns}
              data={filteredData}
              loading={loading}
              onView={(row) => {
                setSelectedNotification(row);
                setOpenModal(true);
              }}
              onEdit={(row) => {
                handleEditNotification(row);
              }}
              onRefresh={confrimResendNotification}
              permissions={permissions}
            />
          </div>

          {/* Pagination with filtered data */}

          <Pagination
            currentPage={currentPage}
            totalPages={meta.totalPages || 0}
            pageSize={limit}
            onPageChange={(page) => setcurrentPage(page)}
            onPageSizeChange={(size) => {
              setLimit(size);
              setcurrentPage(1);
            }}
          />
        </div>

        <ViewDetailsModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          title="Notification Details"
          data={selectedNotification || {}}
          fields={getNotificationDetailFields(selectedNotification)}
        />
      </div>
    </PageHeaderWrapper>
  );
}
